import { supabase } from '../lib/supabase';
import { Player, Payment, Expense, AppConfiguration } from '../types';

// Helper to ensure supabase is available
const ensureSupabase = () => {
  if (!supabase) {
    throw new Error('Database not available - Supabase configuration missing');
  }
  return supabase;
};

// Players API
export const playersApi = {
  async getAll(): Promise<Player[]> {
    const client = ensureSupabase();

    const { data, error } = await client
      .from('players')
      .select('*')
      .order('name');

    if (error) throw error;

    return data.map(player => ({
      id: player.id,
      name: player.name,
      isActive: player.is_active
    }));
  },

  async create(player: Omit<Player, 'id'>): Promise<Player> {
    const client = ensureSupabase();
    const id = Date.now().toString();
    const { data, error } = await client
      .from('players')
      .insert({
        id,
        name: player.name,
        is_active: player.isActive
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      isActive: data.is_active
    };
  },

  async update(id: string, updates: Partial<Player>): Promise<Player> {
    const client = ensureSupabase();
    const { data, error } = await client
      .from('players')
      .update({
        ...(updates.name && { name: updates.name }),
        ...(updates.isActive !== undefined && { is_active: updates.isActive }),
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      isActive: data.is_active
    };
  },

  async delete(id: string): Promise<void> {
    const client = ensureSupabase();
    const { error } = await client
      .from('players')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Payments API
export const paymentsApi = {
  async getAll(): Promise<Payment[]> {
    const client = ensureSupabase();
    const { data, error } = await client
      .from('payments')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(payment => ({
      id: payment.id,
      playerId: payment.player_id,
      categoryId: payment.category_id,
      amount: payment.amount,
      isPaid: payment.is_paid,
      paidAt: payment.paid_at,
      isAdvance: payment.is_advance,
      isPartialPayment: payment.is_partial_payment
    }));
  },

  async create(payment: Omit<Payment, 'id'>): Promise<Payment> {
    const client = ensureSupabase();
    const id = Date.now().toString();
    const { data, error } = await client
      .from('payments')
      .insert({
        id,
        player_id: payment.playerId,
        category_id: payment.categoryId,
        amount: payment.amount,
        is_paid: payment.isPaid,
        paid_at: payment.paidAt,
        is_advance: payment.isAdvance,
        is_partial_payment: payment.isPartialPayment
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      playerId: data.player_id,
      categoryId: data.category_id,
      amount: data.amount,
      isPaid: data.is_paid,
      paidAt: data.paid_at,
      isAdvance: data.is_advance,
      isPartialPayment: data.is_partial_payment
    };
  },

  async update(id: string, updates: Partial<Payment>): Promise<Payment> {
    const client = ensureSupabase();
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (updates.playerId) updateData.player_id = updates.playerId;
    if (updates.categoryId) updateData.category_id = updates.categoryId;
    if (updates.amount !== undefined) updateData.amount = updates.amount;
    if (updates.isPaid !== undefined) updateData.is_paid = updates.isPaid;
    if (updates.paidAt !== undefined) updateData.paid_at = updates.paidAt;
    if (updates.isAdvance !== undefined) updateData.is_advance = updates.isAdvance;
    if (updates.isPartialPayment !== undefined) updateData.is_partial_payment = updates.isPartialPayment;

    const { data, error } = await client
      .from('payments')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      playerId: data.player_id,
      categoryId: data.category_id,
      amount: data.amount,
      isPaid: data.is_paid,
      paidAt: data.paid_at,
      isAdvance: data.is_advance,
      isPartialPayment: data.is_partial_payment
    };
  },

  async delete(id: string): Promise<void> {
    const client = ensureSupabase();
    const { error } = await client
      .from('payments')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Expenses API
export const expensesApi = {
  async getAll(): Promise<Expense[]> {
    const client = ensureSupabase();
    const { data, error } = await client
      .from('expenses')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(expense => ({
      id: expense.id,
      description: expense.description,
      amount: expense.amount,
      paidBy: expense.paid_by,
      categoryId: expense.category_id,
      details: expense.details,
      billImageUrl: expense.bill_image_url,
      createdAt: new Date(expense.created_at)
    }));
  },

  async create(expense: Omit<Expense, 'id'>): Promise<Expense> {
    const client = ensureSupabase();
    const id = Date.now().toString();
    const { data, error } = await client
      .from('expenses')
      .insert({
        id,
        description: expense.description,
        amount: expense.amount,
        paid_by: expense.paidBy,
        category_id: expense.categoryId,
        details: expense.details,
        bill_image_url: expense.billImageUrl
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      description: data.description,
      amount: data.amount,
      paidBy: data.paid_by,
      categoryId: data.category_id,
      details: data.details,
      billImageUrl: data.bill_image_url,
      createdAt: new Date(data.created_at)
    };
  },

  async update(id: string, updates: Partial<Expense>): Promise<Expense> {
    const client = ensureSupabase();
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (updates.description) updateData.description = updates.description;
    if (updates.amount !== undefined) updateData.amount = updates.amount;
    if (updates.paidBy) updateData.paid_by = updates.paidBy;
    if (updates.categoryId) updateData.category_id = updates.categoryId;
    if (updates.details !== undefined) updateData.details = updates.details || null;
    if (updates.billImageUrl !== undefined) updateData.bill_image_url = updates.billImageUrl || null;

    const { data, error } = await client
      .from('expenses')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      description: data.description,
      amount: data.amount,
      paidBy: data.paid_by,
      categoryId: data.category_id,
      details: data.details,
      billImageUrl: data.bill_image_url,
      createdAt: new Date(data.created_at)
    };
  },

  async delete(id: string): Promise<void> {
    const client = ensureSupabase();
    const { error } = await client
      .from('expenses')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};

// Config API
export const configApi = {
  async get(): Promise<AppConfiguration> {
    const client = ensureSupabase();
    const { data, error } = await client
      .from('config')
      .select('*')
      .eq('id', 1)
      .single();

    if (error) throw error;

    return {
      tournamentFee: data.tournament_fee,
      transportTotal: data.transport_total,
      teamName: data.team_name
    };
  },

  async update(updates: Partial<AppConfiguration>): Promise<AppConfiguration> {
    const client = ensureSupabase();
    const updateData: any = {
      updated_at: new Date().toISOString()
    };

    if (updates.tournamentFee !== undefined) updateData.tournament_fee = updates.tournamentFee;
    if (updates.transportTotal !== undefined) updateData.transport_total = updates.transportTotal;
    if (updates.teamName) updateData.team_name = updates.teamName;

    const { data, error } = await client
      .from('config')
      .update(updateData)
      .eq('id', 1)
      .select()
      .single();

    if (error) throw error;

    return {
      tournamentFee: data.tournament_fee,
      transportTotal: data.transport_total,
      teamName: data.team_name
    };
  }
};